import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius)] text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground hover:bg-primary/90",
        secondary: "border-2 border-primary text-primary bg-transparent hover:bg-primary/5",
        tertiary: "text-[rgb(var(--muted-foreground))] hover:bg-[rgb(var(--muted))]",
        /**
         * `SITE-NAV01`: the quiet bordered action on a light surface, the site
         * bar's search, language and menu buttons and the phone menu's Log in.
         * `outlineDark` is the same thing on a dark ground (`docs/03` §6).
         */
        outline: "border border-neutral-200 bg-white text-neutral-800 hover:border-primary hover:text-primary",
        /**
         * `EX-PUB19`: the Judge's dark surfaces (the bar, the Arena landing).
         * `outlineDark` is `secondary` on a dark ground, `ghostDark` is
         * `tertiary`, and `inverse` is the white action that sits beside a
         * primary without competing with it (`docs/03` §6). `EX-EDR02`: the
         * two dark ones show `aria-pressed` as a lighter fill, for a toggle
         * (Custom input) or the one picked of a few (a sample).
         */
        outlineDark:
          "border border-white/20 bg-transparent text-white hover:border-primary hover:text-accent-peach focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950 aria-pressed:border-white/40 aria-pressed:bg-white/15",
        ghostDark:
          "text-neutral-300 hover:bg-white/10 hover:text-white focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950 aria-pressed:bg-white/15 aria-pressed:text-white",
        inverse:
          "bg-white text-neutral-900 hover:bg-neutral-100 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950",
        /**
         * `EX-EDR01` review: an action worded as a link on the dark ground,
         * peach text and a faint fill on hover (the test strip's Details, the
         * result card's My submissions). A component that owns a status colour
         * (the verdict pill) passes `variant={null}` and draws its own tone.
         */
        linkDark:
          "text-accent-peach hover:bg-white/10 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950",
        /**
         * `GR-PRT02`: `linkDark` on a light ground, an action worded as an
         * underlined link inside a card (the checkout's Remove beside an
         * applied code, Enter another code). Neutral ink, so it passes 4.5:1
         * where the brand orange does not at body size.
         */
        link: "text-neutral-800 underline underline-offset-4 hover:text-neutral-950",
        /**
         * One option of a pill-shaped switch, the language pill (`LocalePill`):
         * muted text, and the option `aria-pressed` filled `primary`. `segment`
         * sits on a light track, `segmentDark` on the Judge's near-black. A
         * labelled group of choices with its own track is `SegmentedControl`.
         */
        segment:
          "text-neutral-500 hover:text-neutral-700 focus-visible:ring-offset-2 aria-pressed:bg-primary aria-pressed:text-white aria-pressed:shadow-sm",
        segmentDark:
          "text-neutral-400 hover:text-white focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950 aria-pressed:bg-primary aria-pressed:text-white",
        /**
         * `SITE-CRS01`: one of a few things to pick, drawn as a bordered tile on
         * a light surface (a course's plan in its pricing card, a plan toggle on
         * a phone). The picked one, `aria-checked` in a radiogroup or
         * `aria-pressed` in a toggle row, takes a primary border and wash.
         * `filter` is the catalogue's filter pill, filled primary when on.
         */
        choice:
          "border border-neutral-200 bg-white font-normal text-neutral-900 hover:border-neutral-300 hover:bg-neutral-50 aria-checked:border-primary aria-checked:bg-primary/[0.06] aria-checked:ring-1 aria-checked:ring-primary/25 aria-pressed:border-primary aria-pressed:bg-primary/[0.06] aria-pressed:ring-1 aria-pressed:ring-primary/25",
        filter:
          "border border-neutral-200 bg-white text-neutral-800 hover:border-primary/40 hover:text-primary aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-white",
        /**
         * `EX-SFX04`: a dark filled action on a light surface that must not
         * read as the page's primary, the bench's Ask AI. The orange is kept
         * for what the answer leads to (Put fix in editor), so asking and
         * acting never look like the same step (`docs/03` §6).
         */
        ink: "bg-neutral-900 text-white hover:bg-neutral-800 focus-visible:ring-offset-2",
        /**
         * `HR-JOB01c`: the filled action that ends something for a person, the
         * Hire board's Reject: `ConfirmDialog`'s destructive red as a variant,
         * so a dialog with its own form does not paint the colour at the call
         * site (`docs/03` §6).
         */
        danger: "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600 focus-visible:ring-offset-2",
        /**
         * `QZ-BLD01`: a control with nothing to do right now that says so as
         * a grey fill, the quiz builder's "Saved 10:47" while nothing new
         * waits to be saved. Worn with `aria-disabled` rather than
         * `disabled`, so it keeps its focus and its tooltip (`docs/03` §6).
         */
        muted: "bg-neutral-100 text-neutral-500 hover:bg-neutral-100 aria-disabled:cursor-default",
        /**
         * `EX-MON01`: a sky tag that opens what it counts, the Monitor's
         * "13 pictures" opening the student's pictures. The same sky as the
         * plain count beside it, a shade darker on hover. Used with
         * `size="chipTap"` (`docs/03` §6).
         */
        info: "bg-sky-50 text-sky-800 hover:bg-sky-100 hover:text-sky-900",
        /**
         * `EX-PLR01`: a list's column title that sorts by its column (the
         * library's Solved / Tried), drawn as the other titles beside it and
         * darker while its order is on (`aria-pressed`). Used with
         * `size={null}`: the title's own line is its size (`docs/03` §6).
         */
        columnHead:
          "gap-1.5 rounded-sm p-0 text-[11.5px] font-bold uppercase tracking-[0.1em] text-neutral-400 hover:text-neutral-700 aria-pressed:text-neutral-900",
        /**
         * `QZ-BLD04`: one entry of an outline that jumps within the page (the
         * quiz builder's question rail): grey text, a grey wash on hover, and
         * the entry the reader is on (`aria-current`) a primary wash. Used
         * with `size="navRow"`; admin's `Button` has the same pair (`docs/03` §6).
         */
        navItem:
          "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 aria-[current=true]:bg-primary/[0.07] aria-[current=true]:text-neutral-900",
        /**
         * `QZ-BLD05`: `primary` in a quiz author's colour, the quiz page's
         * and the pre-quiz screen's Start. The fill is `--theme-accent`, an
         * `R G B` triplet a parent sets from a parsed colour through a style
         * object (`quizAccentStyle`), never a string from the server; with no
         * parent setting it, the brand orange. `tab` is the quiz page's
         * Overview and Leaderboard tabs, the one `aria-selected` filled the
         * same way (`docs/03` §6).
         */
        accent:
          "bg-[rgb(var(--theme-accent,233_77_29))] text-white hover:bg-[rgb(var(--theme-accent,233_77_29)/0.9)] focus-visible:ring-[rgb(var(--theme-accent,233_77_29))]",
        tab: "font-semibold text-[rgb(var(--muted-foreground))] hover:bg-[rgb(var(--muted))] aria-selected:bg-[rgb(var(--theme-accent,233_77_29))] aria-selected:text-white aria-selected:hover:bg-[rgb(var(--theme-accent,233_77_29))]",
        /**
         * `QZ-BLD05`: one colour of a palette, the quiz builder's accent
         * swatches, with `size="avatar"` (a 32 px circle, a 44 px hit area).
         * The call site sets the swatch's own colour as `backgroundColor` and
         * `color`, so the picked one (`aria-pressed`) is ringed in itself
         * behind a white gap (`docs/03` §6).
         */
        swatch:
          "border-2 border-white shadow-[inset_0_0_0_1px_rgb(0_0_0/0.12)] focus-visible:ring-offset-2 aria-pressed:ring-2 aria-pressed:ring-current",
        /**
         * `EX-SEC14`: an on/off switch, worn with `role="switch"` and
         * `aria-checked` and with `size="switch"`: a grey track, primary when
         * on. Its knob is the one child, `SWITCH_KNOB`, which slides on the
         * same `aria-checked` (the Monitor's "Send to everyone as a notice",
         * `docs/03` §6).
         */
        switch:
          "group justify-start rounded-full bg-neutral-200 focus-visible:ring-offset-2 aria-checked:bg-primary",
      },
      /**
       * `EX-HST02`: `lg` is the 48 px action of a landing page, the size the
       * Judge landing had been writing as `h-12 px-6 text-base` at each call
       * site; admin's `Button` has the same `lg` (`docs/03` §6).
       *
       * `EX-EDR01` review: the dense sizes of the Judge's editor column, which
       * its files had been writing as raw buttons. `sm` is a 36 px toolbar
       * button (admin's `sm`, the name `lg` already shares) and `smIcon` its
       * square; `mini` and `miniIcon` are the 28 px round buttons of a
       * one-line strip, whose hit area grows to 44 px below `lg`, where the
       * reader is on a touch screen (`docs/03` §12); `pill` is the 40 px round
       * action of the bottom bar; `row` is a whole list row that is one button.
       *
       * The Judge bar on a phone: `icon` is the 44 px square (admin's `icon`),
       * its menu button; `avatar` is the 32 px circle of its account menu,
       * whose hit area is 44 px at every width without taking room in the bar
       * (`docs/03` §6 and §12).
       *
       * The language pill's options, with `segment` or `segmentDark`: `chip` is
       * the 20 px option of a desktop bar, `chipTap` the same option 44 px wide
       * taking taps over 44 px tall (the Judge bar from a tablet up), `chipLg`
       * the 44 px option of a phone menu's row.
       */
      size: {
        default: "px-4 py-2",
        lg: "h-12 px-6 text-base",
        sm: "h-9 gap-1.5 rounded-lg px-2.5 text-xs font-semibold",
        smIcon: "h-9 w-9 rounded-lg p-0",
        mini: "relative h-7 gap-1 rounded-full px-2.5 text-xs font-semibold before:absolute before:-inset-y-2 before:inset-x-0 before:content-[''] lg:before:content-none",
        miniIcon: "relative h-7 w-7 rounded-full p-0 before:absolute before:-inset-2 before:content-[''] lg:before:content-none",
        pill: "h-10 rounded-full px-3.5 font-semibold",
        row: "w-full flex-wrap justify-between whitespace-normal rounded-md px-1 py-2 text-left font-normal",
        icon: "h-11 w-11 p-0",
        avatar: "relative h-8 w-8 rounded-full p-0 before:absolute before:-inset-1.5 before:content-['']",
        chip: "rounded-full px-2 py-0.5 text-xs font-semibold",
        chipTap: "relative min-w-11 rounded-full px-2 py-0.5 text-xs font-semibold before:absolute before:inset-x-0 before:-inset-y-3 before:content-['']",
        chipLg: "min-h-11 rounded-full px-3.5 font-semibold",
        /** `SITE-CRS01`: the `choice` tile, a 44 px row whose label and price sit at its two ends. */
        option: "min-h-11 w-full justify-between gap-3 whitespace-normal rounded-[10px] px-3 py-2.5 text-left",
        /**
         * `EX-PDF02`: the two halves of a split button, an action and the arrow
         * that opens its options (the console's Download PDF). Side by side
         * they read as one control: `split` keeps the left corners, `splitIcon`
         * the right ones behind a hairline. 44 px on a phone, 36 px from `sm`
         * (`docs/03` §6 and §12).
         */
        split: "h-11 gap-1.5 rounded-l-lg rounded-r-none px-3 text-xs font-semibold sm:h-9",
        splitIcon: "h-11 w-11 rounded-l-none rounded-r-lg border-l border-white/40 p-0 sm:h-9 sm:w-9",
        /**
         * `EX-LDB03`: the 44 px action of a page's body, the touch minimum
         * (`docs/03` §12) between `default`'s 36 px and `lg`'s landing-page
         * 48 px: the leaderboards' paging, an empty board's next step and the
         * reader's own place, which had been writing `h-10` at each call site.
         */
        md: "h-11 px-4 font-semibold",
        /**
         * `QZ-BLD03`: a `choice` drawn as a card, a title over a line saying
         * what it means, at least 44 px tall (the quiz builder's kind of test,
         * who can take it, the settings' mode). Admin's `tile` is the same
         * size (`docs/03` §6).
         */
        tile: "h-auto min-h-11 w-full flex-col items-start justify-start gap-1 whitespace-normal rounded-xl px-3.5 py-3 text-left",
        /**
         * `QZ-BLD04`: `navItem`'s row, the width of its list, its content
         * left to right; 44 px tall below `lg` (a touch screen, `docs/03`
         * §12) and 36 px from there. A card's header that opens the card (a
         * quiz builder question) is the same row with `tertiary`.
         */
        navRow: "h-auto min-h-11 w-full justify-start gap-2 whitespace-normal rounded-lg px-2 py-1.5 text-left text-[13px] font-normal lg:min-h-9",
        /**
         * `SUP-UI01`: a picture that is one button, a 96 px square whose image
         * fills it and opens it larger (a support message's screenshot), worn
         * with `outline`. Admin's `thumb` is the same size (`docs/03` §6).
         */
        thumb: "h-24 w-24 overflow-hidden rounded-lg p-0",
        /**
         * `EX-PP01`: a sitting's screen picture in the teacher's snapshot grid,
         * a 16:10 tile as wide as its column whose image fills it and opens it
         * in the lightbox, worn with `outline` (`docs/03` §6).
         */
        shot: "relative block aspect-[16/10] h-auto w-full overflow-hidden rounded-[10px] p-0",
        /**
         * `SUP-AI03`: the help launcher, a round 48 px action that floats in
         * the corner of every page: a circle on a phone, a pill with its
         * word from `sm` (`docs/03` §6).
         */
        launcher:
          "h-12 w-12 rounded-full p-0 font-semibold shadow-[0_8px_30px_rgba(0,0,0,0.06)] ring-1 ring-black/5 sm:w-auto sm:px-5",
        /**
         * `EX-SEC14`: `switch`'s track, 44 by 24 px with the knob inset 2 px,
         * taking taps over 44 px tall (`docs/03` §12).
         */
        switch: "relative h-6 w-11 shrink-0 p-0.5 before:absolute before:inset-x-0 before:-inset-y-2.5 before:content-['']",
      },
    },
    defaultVariants: { variant: "primary", size: "default" },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

/** `EX-SEC14`: the knob inside a `variant="switch"` button, right when it is on. */
const SWITCH_KNOB =
  "pointer-events-none h-5 w-5 rounded-full bg-white shadow-sm transition-transform group-aria-checked:translate-x-5";

export { Button, buttonVariants, SWITCH_KNOB };
